/**
 * Mobile Menu Component
 *
 * Mobile navigation menu with sheet overlay.
 *
 * Client Component: Uses Sheet (Radix UI Dialog) for interactive overlay
 * and useState for menu open/close state.
 */

"use client";

import { Menu } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { PhoneButton } from "@/components/ui/phone-button";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@/components/ui/sheet";
import type { Navigation, SiteMetadata } from "@/lib/validation";

interface MobileMenuProps {
	navigation: Navigation;
	siteMetadata: SiteMetadata;
}

export function MobileMenu({ navigation, siteMetadata }: MobileMenuProps) {
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

	return (
		<div className="lg:hidden">
			<Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
				<SheetTrigger asChild>
					<Button variant="ghost" size="icon">
						<Menu className="size-6" />
						<span className="sr-only">Open menu</span>
					</Button>
				</SheetTrigger>
				<SheetContent side="right">
					<SheetHeader>
						<SheetTitle>Menu</SheetTitle>
					</SheetHeader>

					<nav className="flex min-w-0 flex-1 flex-col gap-4 overflow-y-auto p-4 pt-0">
						<ul className="flex min-w-0 flex-col">
							{navigation.mainMenu.map((item) => (
								<li key={item.href} className="min-w-0">
									<Link
										href={item.href}
										className="block truncate rounded-md px-3 py-3 text-base font-medium transition-colors hover:bg-muted active:bg-muted/80"
										onClick={() => setMobileMenuOpen(false)}
									>
										{item.label}
									</Link>
								</li>
							))}
						</ul>

						<div className="mt-auto flex flex-col gap-4">
							<hr className="border-border" />
							<PhoneButton
								phoneNumber={siteMetadata.sitePhone}
								variant="default"
								size="default"
								className="w-full"
							/>
						</div>
					</nav>
				</SheetContent>
			</Sheet>
		</div>
	);
}
